import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "./Toast";
import { COGNITO } from "../config";
import { getValidAccessToken } from "../auth/client";
import { PasskeyEnroll } from "./PasskeyEnroll";

interface Credential {
  credential_id: string;
  friendly_name?: string;
  created_at?: string;
}

async function fido2(path: string, init: RequestInit = {}): Promise<any> {
  const token = await getValidAccessToken();
  if (!token) throw new Error("Not signed in");
  const res = await fetch(`${COGNITO.apiBase}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(init.headers || {}) },
  });
  const text = await res.text();
  if (!res.ok) throw new Error(`${res.status} ${text}`);
  return text ? JSON.parse(text) : null;
}

/**
 * Settings → Passkeys. Adding a passkey while signed in is the supported
 * enrollment path (also how a therapist gets a passkey onto the phone app:
 * save it to Google Password Manager, or choose "Use a phone").
 */
export const PasskeysSection: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [creds, setCreds] = useState<Credential[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const r = await fido2("/fido2/credentials");
      setCreds(r.credentials);
    } catch (e) {
      console.error("loading passkeys failed", e);
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (c: Credential) => {
    if (!window.confirm(t("settings.passkeys.removeConfirm", { name: c.friendly_name || c.credential_id.slice(0, 8) }))) return;
    try {
      await fido2(`/fido2/credentials/${encodeURIComponent(c.credential_id)}`, { method: "DELETE" });
      await load();
    } catch (e) {
      console.error("removing passkey failed", e);
      toast.error(t("settings.passkeys.removeFailed"));
    }
  };

  const title = { fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--color-title)" } as const;
  const hint = { fontSize: 12, color: "var(--color-muted)", marginBottom: 10 } as const;
  const button = {
    padding: "8px 14px", background: "var(--color-bg)", border: "1px solid var(--color-border)",
    borderRadius: 4, fontSize: 13, cursor: "pointer", color: "var(--color-ink)",
  } as const;

  if (adding) {
    return (
      <PasskeyEnroll
        onEnrolled={() => { setAdding(false); load(); }}
        onCancel={() => setAdding(false)}
      />
    );
  }

  return (
    <div>
      <div style={title}>{t("settings.passkeys.title")}</div>
      <div style={hint}>{t("settings.passkeys.hint")}</div>
      {loadError && <div style={{ ...hint, color: "var(--color-danger)" }}>{t("settings.passkeys.loadFailed")}</div>}
      {creds && creds.length === 0 && <div style={hint}>{t("settings.passkeys.none")}</div>}
      {creds && creds.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 10px", display: "flex", flexDirection: "column", gap: 6 }}>
          {creds.map((c) => (
            <li key={c.credential_id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
              <span style={{ flex: 1, color: "var(--color-ink)" }}>
                {c.friendly_name || t("settings.passkeys.unnamed")}
                {c.created_at && <span style={{ color: "var(--color-muted)" }}> · {new Date(c.created_at).toLocaleDateString()}</span>}
              </span>
              <button style={button} onClick={() => remove(c)}>{t("settings.passkeys.remove")}</button>
            </li>
          ))}
        </ul>
      )}
      <button style={button} onClick={() => setAdding(true)}>{t("settings.passkeys.add")}</button>
    </div>
  );
};
